import { useEffect, useState } from "react";
import { useAutoOpen } from "@/hooks/useAutoOpen";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/app/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import Pagination, { usePagination } from "@/components/app/Pagination";

const empty = { nama: "", no_wa: "", email: "", alamat: "" };

export default function MasterPembeli() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => { const r = await api.get("/pembeli"); setItems(r.data); };
  useEffect(()=>{ load(); },[]);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const openNew = () => { setEditId(null); setForm(empty); setOpen(true); };
  useAutoOpen(openNew);
  const openEdit = (p) => { setEditId(p.id); setForm({...empty, ...p}); setOpen(true); };

  const save = async () => {
    if (!form.nama.trim()) { toast.error("Nama wajib"); return; }
    try {
      if (editId) await api.put(`/pembeli/${editId}`, form);
      else await api.post("/pembeli", form);
      setOpen(false); setForm(empty); await load();
      toast.success(editId?"Data diperbarui":"Pembeli tersimpan");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const delOne = async (id) => { if (!confirm("Hapus pembeli?")) return; await api.delete(`/pembeli/${id}`); load(); toast.success("Terhapus"); };
  const toggle = (id) => { const s=new Set(selected); s.has(id)?s.delete(id):s.add(id); setSelected(s); };
  const bulkDel = async () => {
    if (!selected.size || !confirm(`Hapus ${selected.size} pembeli?`)) return;
    await api.post("/pembeli/bulk-delete", { ids: Array.from(selected) });
    setSelected(new Set()); await load(); toast.success("Terhapus");
  };
  const { page, setPage, totalPages, paged, total, pageSize } = usePagination(items, 10);

  return (
    <>
      <PageHeader title="Master Data Pembeli" subtitle="Kelola daftar pembeli/pelanggan Anda." actions={
        <>
          {selected.size>0 && <Button variant="outline" onClick={bulkDel} className="rounded-sm" data-testid="bulk-del-pembeli"><Trash2 size={14} className="mr-1"/> Hapus ({selected.size})</Button>}
          <Button onClick={openNew} className="brand-btn rounded-sm" data-testid="add-pembeli-btn"><Plus size={14} className="mr-1"/> Tambah</Button>
        </>
      }/>
      <div className="p-4 md:p-8">
        <div className="card-flat overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-xs md:text-sm">
            <thead><tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] uppercase tracking-wide text-slate-500">
              <th className="p-3 w-10"></th><th className="p-3">Nama</th><th className="p-3">No. WA</th>
              <th className="p-3 hide-mobile">Email</th><th className="p-3 hide-mobile">Alamat</th>
              <th className="p-3 text-right">Aksi</th>
            </tr></thead>
            <tbody>
              {items.length===0 && <tr><td colSpan="6" className="text-center py-10 text-slate-400">Belum ada data pembeli.</td></tr>}
              {paged.map(p=>(
                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="p-3"><Checkbox checked={selected.has(p.id)} onCheckedChange={()=>toggle(p.id)}/></td>
                  <td className="p-3 font-medium">{p.nama}</td>
                  <td className="p-3 font-mono text-[13px]">{p.no_wa||"-"}</td>
                  <td className="p-3 hide-mobile">{p.email||"-"}</td>
                  <td className="p-3 text-slate-500 hide-mobile">{p.alamat||"-"}</td>
                  <td className="p-3">
                    <div className="flex justify-end gap-1">
                      <button onClick={()=>openEdit(p)} className="p-1.5 rounded-sm hover:bg-slate-100"><Pencil size={14}/></button>
                      <button onClick={()=>delOne(p.id)} className="p-1.5 rounded-sm hover:bg-rose-50"><Trash2 size={14} className="text-rose-600"/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <Pagination page={page} setPage={setPage} totalPages={totalPages} total={total} pageSize={pageSize} testid="pembeli-pagination"/>
        </div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="pembeli-dialog">
          <DialogHeader><DialogTitle>{editId?"Ubah":"Tambah"} Pembeli</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nama *</Label><Input value={form.nama} onChange={upd("nama")} data-testid="pembeli-nama"/></div>
            <div><Label>No. WhatsApp</Label><Input value={form.no_wa} onChange={upd("no_wa")}/></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={upd("email")}/></div>
            <div><Label>Alamat (opsional)</Label><Textarea value={form.alamat} onChange={upd("alamat")}/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)} className="rounded-sm">Batal</Button>
            <Button onClick={save} className="brand-btn rounded-sm" data-testid="pembeli-save">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
